import { difference, groupBy, toPairs, union } from 'lodash';
import { pipe } from 'ramda';
import React, { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import {
  BulkTaskPayload,
  FieldValueType,
  Task as NormalizedTask,
  TaskAPI,
} from 'api/tasks';
import { map, ofNullable, orElse } from 'core/maybe';
import { RootState } from 'data';
import { FetchStatus } from 'data/core';
import { saveTasks } from 'data/tasks/slice';
import {
  ASSIGNEE_VALUE_TRANSFORMER,
  MULTI_USER_TRANSFORMER,
  SINGLE_USER_TRANSFORMER,
  useProjectInvitationHandler,
  UserIdTransformer,
} from 'hooks/use-invite-user';
import { logger } from 'services';
import { Empty } from 'stores/core';
import { FieldsStore, TaskField } from 'stores/fields';
import { revert, Task, updated, updating } from 'stores/tasks';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { queueMacrotask } from 'utils/macrotask';
import { toAssigneeValues } from 'utils/to-store-task';
import { getComparator } from './comparators';
import {
  AssigneeField,
  AssigneeValue,
  FIELD_ID_ASSIGNEE,
  FieldType,
  UpdatableSquad,
} from './fields';

export type TaskFieldValue = FieldValueType | AssigneeValue[];

export interface PartialValue {
  field: TaskField;
  from?: TaskFieldValue;
  to: TaskFieldValue;
}

type FieldPatch = { fieldId: number; fieldValue: FieldValueType };
type Patch = Record<string, unknown> & { fields?: FieldPatch[] } & {
  assignee?: UpdatableSquad;
};

const _patch = ({
  field: { customField, fieldId, fieldName },
  to,
}: PartialValue): Patch =>
  customField
    ? { fields: [{ fieldId, fieldValue: to as FieldValueType }] }
    : fieldName === 'assignee'
    ? { assignee: AssigneeValue.toSquad(to as AssigneeValue[]) }
    : fieldName === 'project'
    ? {}
    : { [fieldName]: to as FieldValueType };

export const PartialValue = {
  toPatch: (values: PartialValue[]): Record<string, unknown> =>
    values.reduce((patch, value) => {
      const { fields = [], ...rest } = _patch(value);

      return {
        ...patch,
        ...rest,
        fields: union(patch.fields ?? [], fields),
      };
    }, {} as Patch),
};

export interface TaskUpdateRequest {
  record: Record<string, unknown>;
  updates: PartialValue[];
  callback?: VoidFunction;
}

const UPDATE_TRANSFORMER: UserIdTransformer<PartialValue> = {
  from: (value, update) => {
    switch (update.field.type) {
      case FieldType.Assignee:
        return {
          ...update,
          to: ASSIGNEE_VALUE_TRANSFORMER.from(
            value,
            (update.to as AssigneeValue[] | null) ?? [],
          ),
        };
      case FieldType.SingleUser:
        return {
          ...update,
          to: SINGLE_USER_TRANSFORMER.from(value, update.to as number),
        };
      case FieldType.MultiUser:
        return {
          ...update,
          to: MULTI_USER_TRANSFORMER.from(
            value,
            (update.to as number[] | null) ?? [],
          ),
        };
      default:
        return update;
    }
  },
  to: (update) => {
    switch (update.field.type) {
      case FieldType.Assignee:
        return ASSIGNEE_VALUE_TRANSFORMER.to(
          (update.to as AssigneeValue[] | null) ?? [],
        );
      case FieldType.SingleUser:
        return SINGLE_USER_TRANSFORMER.to(update.to as number | null);
      case FieldType.MultiUser:
        return MULTI_USER_TRANSFORMER.to((update.to as number[] | null) ?? []);
      default:
        return [];
    }
  },
};

export const UPDATES_TRANSFORMER: UserIdTransformer<PartialValue[]> = {
  from: (value, updates) =>
    updates.map((update) => UPDATE_TRANSFORMER.from(value, update)),
  to: (updates) =>
    updates.reduce(
      (user_ids, update) => union(user_ids, UPDATE_TRANSFORMER.to(update)),
      [] as number[],
    ),
};

const REQUEST_TRANSFORMER: UserIdTransformer<TaskUpdateRequest> = {
  from: (value, { record, updates, callback }) => ({
    record,
    updates: UPDATES_TRANSFORMER.from(value, updates),
    callback,
  }),
  to: ({ updates }) => UPDATES_TRANSFORMER.to(updates),
};

const BULK_TRANSFORMER: UserIdTransformer<TaskUpdateRequest[]> = {
  from: (value, requests) =>
    requests.map((request) => REQUEST_TRANSFORMER.from(value, request)),
  to: (requests) =>
    requests.reduce(
      (user_ids, request) => union(user_ids, REQUEST_TRANSFORMER.to(request)),
      [] as number[],
    ),
};

export const getAddedUsersFromUpdates = (updates: PartialValue[]): number[] =>
  updates.reduce((userIds, update) => {
    if (update.field.type === FieldType.Assignee) {
      const from =
        ASSIGNEE_VALUE_TRANSFORMER.to(
          (update.from as AssigneeValue[] | null) ?? [],
        ) ?? [];
      const to =
        ASSIGNEE_VALUE_TRANSFORMER.to(
          (update.to as AssigneeValue[] | null) ?? [],
        ) ?? [];

      return union(userIds, difference(to, from));
    }

    return userIds;
  }, [] as number[]);

export const getNewlyAddedUsers = (requests: TaskUpdateRequest[]): number[] => {
  return requests.reduce((ids, request) => {
    return union(ids, getAddedUsersFromUpdates(request.updates));
  }, [] as number[]);
};

export const useTaskAssigneeField = () => {
  const fieldsStore = useSelector(({ fields }: RootState) => fields);

  return FieldsStore.getTaskFieldByName('assignee')(fieldsStore)[1] as
    | AssigneeField
    | undefined;
};

export const useUpdateTasks = ({ isAllTask } = { isAllTask: false }) => {
  const dispatch = useDispatch();
  const assignee = useTaskAssigneeField();
  const { byId } = useSelector(({ projects_v2 }: RootState) => projects_v2);
  const handleBulkUpdates = useProjectInvitationHandler(
    BULK_TRANSFORMER,
    getNewlyAddedUsers,
  );

  const with_invitations_handled_for_project = useCallback(
    async (
      projectId: ProjectId,
      updates: TaskUpdateRequest[],
    ): Promise<TaskUpdateRequest[]> => {
      const worker = callWorkerWithCleanup();
      const notInProjectUsers = await worker.workerApi
        .getUsersNotInProject(projectId[1])
        .finally(() => {
          worker.cleanup();
        });

      return handleBulkUpdates(
        updates,
        projectId,
        notInProjectUsers,
        pipe(
          () => projectId[1],
          (id) => byId[id] ?? Empty(),
          (content) => content[1]?.projectName ?? null,
          ofNullable,
          map((x) => <strong>{x}</strong>),
          orElse<ReactNode>(null),
        )(),
      );
    },
    [handleBulkUpdates, byId],
  );

  const with_invitations_handled = useCallback(
    async (requests: TaskUpdateRequest[]): Promise<TaskUpdateRequest[]> => {
      const by_project = toPairs(
        groupBy(requests, (request) => request.record.project as number),
      );

      return await by_project.reduce(
        (promise, [project_id, project_requests]) =>
          promise.then(async (handled) => {
            const results = await with_invitations_handled_for_project(
              ProjectId(parseInt(project_id, 10)),
              project_requests,
            );

            return handled.concat(results);
          }),
        Promise.resolve<TaskUpdateRequest[]>([]),
      );
    },
    [with_invitations_handled_for_project],
  );

  const to_next_task = ({ record, updates }: TaskUpdateRequest) =>
    updates.reduce(
      (next_record, { field: { fieldName }, to }) =>
        ({
          ...next_record,
          [fieldName]: to,
        } as Task),
      record as unknown as Task,
    );

  const updateMultipleTasks = useCallback(
    (requests: TaskUpdateRequest[]) =>
      queueMacrotask(async () => {
        const invitations_handled = await with_invitations_handled(requests);
        const next_tasks = invitations_handled.map(to_next_task);

        dispatch(updating(next_tasks));
        const bulkUpdatePayload: BulkTaskPayload['tasks'] = [];

        invitations_handled.map(async ({ record, updates, callback }) => {
          const taskId = record.taskId as number;

          const next_task = to_next_task({ record, updates });
          const no_change_required = ({ field, from, to }: PartialValue) =>
            from === to || (getComparator(field)?.(from, to) ?? -1) === 0;

          if (updates.every(no_change_required)) {
            dispatch(updated([next_task]));
            callback?.();

            return;
          }
          bulkUpdatePayload.push({
            taskId,
            ...PartialValue.toPatch(updates),
          });
          callback?.();
        });
        TaskAPI.bulkUpdateTask({ tasks: bulkUpdatePayload }, { isAllTask });
      }),
    [dispatch, isAllTask, with_invitations_handled],
  );

  const updateSingleTask = useCallback(
    (requests: TaskUpdateRequest[]) =>
      queueMacrotask(async () => {
        const invitations_handled = await with_invitations_handled(requests);
        const next_tasks = invitations_handled.map(to_next_task);

        dispatch(updating(next_tasks));
        await Promise.all(
          invitations_handled.map(async ({ record, updates, callback }) => {
            const projectId = record.project as number;
            const taskId = record.taskId as number;
            const no_change_required = ({ field, from, to }: PartialValue) =>
              from === to ||
              (getComparator(
                /**
                 * assignee field received as part of updates is not the latest
                 * hence this workaround
                 * TODO: to debug why latest assignee field is not returned
                 * As far analysed, it is due to class components used for ag grid field editor
                 * need to confirm the same and debug/ fix the root cause.
                 */
                field.fieldId === FIELD_ID_ASSIGNEE && assignee
                  ? assignee
                  : field,
                projectId,
              )?.(from, to) ?? -1) === 0;
            const next_task = to_next_task({ record, updates });

            if (updates.every(no_change_required)) {
              dispatch(updated([next_task]));
              callback?.();

              return;
            }
            try {
              let updated_task: NormalizedTask;

              if (PartialValue.toPatch(updates).projectPhaseId) {
                updated_task = await TaskAPI.updatePhase(taskId, {
                  projectPhase: {
                    projectPhaseId: PartialValue.toPatch(updates)
                      .projectPhaseId as number,
                  },
                });
              } else {
                updated_task = await TaskAPI.updateTask(
                  projectId,
                  taskId,
                  PartialValue.toPatch(updates),
                );
              }
              dispatch(
                updated([
                  {
                    ...next_task,
                    // assignee is different in api response when resolved placeholder is assigned. TODO: need to handle in better way to persist entire response instead of assignee value only
                    assignee: toAssigneeValues(updated_task.assignee),
                  } as unknown as Task,
                ]),
              );
              dispatch(
                saveTasks({
                  data: [updated_task],
                  fetchStatus: FetchStatus.Partial,
                }),
              );
              callback?.();
            } catch (error) {
              dispatch(revert([taskId]));
              logger.error('update row failed', { error: error as Error });
            }
          }),
        );
      }),
    [assignee, dispatch, with_invitations_handled],
  );

  return { updateMultipleTasks, updateSingleTask };
};
